// ROOMS: the lobby. Official rooms and rooms players created, with filters,
// quick join, invite codes and the create room dialog.

import { useState } from 'preact/hooks';
import type { RoomInfo } from '../../shared/net/protocol';
import { Button } from '../components/Button';
import { ColorText } from '../components/ColorText';
import { Icon } from '../components/Icon';
import { JoinCode } from '../components/JoinCode';
import { Levelshot } from '../components/Levelshot';
import { useApp } from '../context';
import { stripColors } from '../etColors';
import { createRoomDialog, net } from '../state';

type RoomFilter = 'all' | 'players' | 'free' | 'official';

const FILTERS: { id: RoomFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'players', label: 'With players' },
  { id: 'free', label: 'Free slots' },
  { id: 'official', label: 'Official' },
];

export function RoomRow({ room }: { room: RoomInfo }) {
  const app = useApp();
  const info = net.value.maps.find((m) => m.name === room.map);
  const full = room.players >= room.maxPlayers;
  return (
    <article class={`room-row${room.players ? ' active' : ''}`}>
      <Levelshot map={room.map} class="room-shot" />
      <div class="room-main">
        <div class="room-name">
          {room.name}
          {room.official && <span class="chip static chip-info">Official</span>}
          {room.private && (
            <span class="chip static" title="Private: join with the invite code">
              Private
            </span>
          )}
        </div>
        <div class="room-map">
          {info?.longname ? <ColorText text={info.longname} /> : room.map}
          <span class="mono muted"> · {room.map}</span>
        </div>
        {room.owner && <div class="muted small">Owner: {room.owner}</div>}
      </div>
      <div class="room-players" aria-label={`${room.players} of ${room.maxPlayers} players`}>
        <Icon name="users" size={16} />
        <span class="mono">
          {room.players}/{room.maxPlayers}
        </span>
      </div>
      <Button variant={room.players ? 'primary' : 'secondary'} disabled={full} onClick={() => app.joinRoom({ id: room.id })}>
        {full ? 'Full' : 'Join'}
      </Button>
    </article>
  );
}

export function RoomsScreen() {
  const app = useApp();
  const n = net.value;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<RoomFilter>('all');

  if (n.status !== 'online') return <OfflineNotice />;

  const q = query.trim().toLowerCase();
  const titles = new Map(n.maps.map((m) => [m.name, stripColors(m.longname ?? m.name).toLowerCase()]));
  const shown = n.rooms.filter((r) => {
    if (filter === 'players' && !r.players) return false;
    if (filter === 'free' && r.players >= r.maxPlayers) return false;
    if (filter === 'official' && !r.official) return false;
    return !q || r.name.toLowerCase().includes(q) || r.map.includes(q) || (titles.get(r.map) ?? '').includes(q) || (r.owner ?? '').toLowerCase().includes(q);
  });
  const players = n.rooms.reduce((a, r) => a + r.players, 0);

  return (
    <div class="screen rooms-screen">
      <div class="filter-bar">
        <label class="search">
          <Icon name="search" size={16} />
          <input type="search" class="input" placeholder="Search rooms, maps, players…" value={query} aria-label="Search rooms" spellcheck={false} onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <div class="chips" role="radiogroup" aria-label="Filter">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" role="radio" aria-checked={filter === f.id} class={`chip${filter === f.id ? ' active' : ''}`} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <div class="filter-actions">
          <JoinCode />
          <Button variant="secondary" icon="play" onClick={() => app.quickPlay()}>
            Quick play
          </Button>
          <Button variant="primary" icon="users" onClick={() => (createRoomDialog.value = { map: null })}>
            Create room
          </Button>
        </div>
      </div>
      <div class="result-count muted">
        {n.rooms.length} rooms · {players} playing on {n.serverName}
      </div>
      {shown.length ? (
        <div class="room-list">
          {shown.map((r) => (
            <RoomRow key={r.id} room={r} />
          ))}
        </div>
      ) : (
        <div class="empty-state">
          <Icon name="users" size={28} />
          <p>{n.rooms.length ? 'No rooms match.' : 'No rooms yet: create one.'}</p>
        </div>
      )}
    </div>
  );
}

/** Shown where the server is needed while offline. */
export function OfflineNotice() {
  const app = useApp();
  const n = net.value;
  const seconds = n.retryAt ? Math.max(0, Math.round((n.retryAt - Date.now()) / 1000)) : null;
  return (
    <div class="empty-state offline-notice">
      <Icon name="server" size={28} />
      <p>
        {n.status === 'connecting' ? 'Connecting to the server…' : 'The server is not reachable.'}
        {n.error && n.status === 'offline' && <span class="muted"> ({n.error})</span>}
      </p>
      {n.status === 'offline' && (
        <>
          <Button variant="primary" onClick={() => app.connect()}>
            Retry now
          </Button>
          {seconds !== null && <p class="muted small">Retrying automatically{seconds ? ` in ${seconds} s` : ''}. Maps on this device can be played offline from MAPS.</p>}
        </>
      )}
    </div>
  );
}
